<!--
 * @Description: 营销管理/活动运营报表/用户运营报表
-->
<template>
  <scroll-layout>
    <div class="user-operation-report">
      <!-- Tab切换 -->
      <ykc-tabs
        :tabList="tabList"
        active="monthly"
        :headerButtonStatus="false"
        @tabClick="handleTabClick">
        <div slot="monthly" class="tab-panel">
          <div class="panel-header">
            <h3 class="panel-title">用户运营月度报表导出</h3>
            <p class="panel-desc">按月度统计用户运营及营销活动的运营数据，支持批量导出</p>
          </div>

          <div class="filter-section">
            <div class="section-title">筛选条件</div>
            <div class="filter-row">
              <!-- 时间区间 -->
              <div class="filter-item">
                <label class="filter-label">时间区间</label>
                <div class="filter-content">
                  <el-date-picker
                    v-model="monthlyForm.monthRange"
                    type="monthrange"
                    format="yyyy-MM"
                    value-format="yyyy-MM"
                    range-separator="至"
                    start-placeholder="开始月份"
                    end-placeholder="结束月份"
                    :picker-options="monthPickerOptions"
                    @change="handleMonthRangeChange" />
                  <p class="filter-tip">时间跨度最多6个月</p>
                </div>
              </div>

              <!-- 电站名称 -->
              <div class="filter-item">
                <label class="filter-label">电站名称</label>
                <div class="filter-content">
                  <ykc-tree
                    ref="monthlyStationTree"
                    :data="stationCityData"
                    :props="stationProps"
                    @check-change="handleMonthlyStationChange" />
                  <p class="filter-tip">默认为空对应全量电站</p>
                </div>
              </div>
            </div>
          </div>

          <!-- 导出按钮 -->
          <div class="export-section">
            <ykc-button
              type="primary"
              :loading="monthlyExporting"
              v-rbac="'marketing:activityReport:userOperation:export'"
              @click="exportReport('monthly')">
              <i class="el-icon-download"></i>
              导出报表
            </ykc-button>
            <span class="export-tip">导出后将跳转至下载中心</span>
          </div>
        </div>

        <div slot="weekly" class="tab-panel">
          <div class="panel-header">
            <h3 class="panel-title">用户运营周报表导出</h3>
            <p class="panel-desc">按周统计用户运营及营销活动的运营数据，支持批量导出</p>
          </div>

          <div class="filter-section">
            <div class="section-title">筛选条件</div>
            <div class="filter-row">
              <!-- 时间区间 -->
              <div class="filter-item">
                <label class="filter-label">时间区间</label>
                <div class="filter-content">
                  <div class="week-range-wrap">
                    <el-date-picker
                      v-model="weeklyForm.weekStart"
                      type="week"
                      format="yyyy-MM-dd"
                      placeholder="选择开始周"
                      class="week-picker"
                      :picker-options="weekPickerOptions"
                      @change="handleWeekStartChange" />
                    <span v-if="weeklyForm.weekStart" class="week-tag">
                      {{ dateToWeekValue(weeklyForm.weekStart) }}
                    </span>
                    <span class="range-separator">至</span>
                    <el-date-picker
                      v-model="weeklyForm.weekEnd"
                      type="week"
                      format="yyyy-MM-dd"
                      placeholder="选择结束周"
                      class="week-picker"
                      :picker-options="weekPickerOptions"
                      @change="handleWeekEndChange" />
                    <span v-if="weeklyForm.weekEnd" class="week-tag">
                      {{ dateToWeekValue(weeklyForm.weekEnd) }}
                    </span>
                  </div>
                  <p class="filter-tip">时间跨度最多30周</p>
                </div>
              </div>

              <!-- 电站名称 -->
              <div class="filter-item">
                <label class="filter-label">电站名称</label>
                <div class="filter-content">
                  <ykc-tree
                    ref="weeklyStationTree"
                    :data="stationCityData"
                    :props="stationProps"
                    @check-change="handleWeeklyStationChange" />
                  <p class="filter-tip">默认为空对应全量电站</p>
                </div>
              </div>
            </div>
          </div>

          <!-- 导出按钮 -->
          <div class="export-section">
            <ykc-button
              type="primary"
              :loading="weeklyExporting"
              v-rbac="'marketing:activityReport:userOperation:export'"
              @click="exportReport('weekly')">
              <i class="el-icon-download"></i>
              导出报表
            </ykc-button>
            <span class="export-tip">导出后将跳转至下载中心</span>
          </div>
        </div>
      </ykc-tabs>

      <!-- 操作提示 -->
      <div class="tip-card">
        <div class="tip-content">
          <i class="el-icon-info tip-icon"></i>
          <div class="tip-text">
            <p class="tip-title">操作说明</p>
            <ul class="tip-list">
              <li>月度报表：时间跨度最多可选择6个月</li>
              <li>周报表：时间跨度最多可选择30周</li>
              <li>电站名称支持多选和模糊搜索，可按城市批量选择</li>
              <li>导出任务执行期间，请勿重复提交相同类型的导出请求</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </scroll-layout>
</template>

<script>
  import { offlineExport } from '@/utils';
  import { treeUtils } from '@/service/apis';

  export default {
    name: 'userOperationReportList',
    data() {
      return {
        activeTab: 'monthly',
        monthlyExporting: false,
        weeklyExporting: false,
        chartExporting: false,
        stationCityData: [],
        stationProps: {
          label: 'name',
          children: 'subNodes',
          disabled: 'disabled',
        },
        monthlyForm: {
          monthRange: [],
          stationIds: [],
        },
        weeklyForm: {
          weekStart: null,
          weekEnd: null,
          stationIds: [],
        },
        tabList: [
          { label: '用户运营月度报表', name: 'monthly' },
          { label: '用户运营周报表', name: 'weekly' },
        ],
        monthPickerOptions: {
          disabledDate: date => {
            return date && date.getTime() > Date.now();
          },
        },
        weekPickerOptions: {
          disabledDate: date => {
            return date && date.getTime() > Date.now();
          },
        },
      };
    },
    created() {
      this.loadStationData();
      this.initDefaultMonthRange();
      this.initDefaultWeekRange();
    },
    methods: {
      /**
       * 初始化默认月份范围（最近2个月）
       */
      initDefaultMonthRange() {
        const end = new Date();
        const start = new Date();
        start.setMonth(start.getMonth() - 1);
        const format = d => {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          return `${year}-${month}`;
        };
        this.monthlyForm.monthRange = [format(start), format(end)];
      },
      /**
       * 初始化默认周范围（最近2周）
       */
      initDefaultWeekRange() {
        const now = new Date();
        this.weeklyForm.weekEnd = new Date(now);
        const lastWeek = new Date(now);
        lastWeek.setDate(now.getDate() - 7);
        this.weeklyForm.weekStart = new Date(lastWeek);
      },
      /**
       * 开始周变化，自动修正超出范围的结束周
       */
      handleWeekStartChange(val) {
        if (!val || !this.weeklyForm.weekEnd) return;
        if (val.getTime() > this.weeklyForm.weekEnd.getTime()) {
          this.weeklyForm.weekEnd = new Date(val);
          return;
        }
        const diffWeek = Math.ceil(
          (this.weeklyForm.weekEnd.getTime() - val.getTime()) / (7 * 86400000)
        );
        if (diffWeek > 29) {
          const end = new Date(val);
          end.setDate(val.getDate() + 29 * 7);
          this.weeklyForm.weekEnd = end;
        }
      },
      /**
       * 结束周变化，自动修正超出范围的开始周
       */
      handleWeekEndChange(val) {
        if (!val || !this.weeklyForm.weekStart) return;
        if (val.getTime() < this.weeklyForm.weekStart.getTime()) {
          this.weeklyForm.weekStart = new Date(val);
          return;
        }
        const diffWeek = Math.ceil(
          (val.getTime() - this.weeklyForm.weekStart.getTime()) / (7 * 86400000)
        );
        if (diffWeek > 29) {
          const start = new Date(val);
          start.setDate(val.getDate() - 29 * 7);
          this.weeklyForm.weekStart = start;
        }
      },
      /**
       * Date 转 YYYYWW 格式周字符串（用于导出）
       */
      dateToWeekValue(date) {
        if (!date) return '';
        const d = new Date(date);
        const dayOfWeek = (d.getDay() + 6) % 7;
        const thursday = new Date(d);
        thursday.setDate(d.getDate() - dayOfWeek + 3);
        const year = thursday.getFullYear();
        const jan1 = new Date(year, 0, 1);
        const firstThursday = new Date(year, 0, 1 + ((4 - jan1.getDay() + 7) % 7));
        const weekNum = 1 + Math.ceil((thursday - firstThursday) / 604800000);
        const weekStr = String(weekNum).padStart(2, '0');
        return `${year}${weekStr}`;
      },
      /**
       * Tab 切换
       */
      handleTabClick(tab) {
        this.activeTab = tab;
      },
      /**
       * 加载电站数据
       */
      loadStationData() {
        treeUtils
          .findAllCityStationTree({})
          .then(res => {
            if (res && res.length) {
              this.stationCityData = this.formatStationCityTree(res);
            }
          })
          .catch(err => {
            console.error('加载电站数据失败:', err);
          });
      },
      /**
       * 格式化城市电站树数据（转换为 ykc-tree 所需格式）
       */
      formatStationCityTree(data) {
        if (!data || !data.length) return [];
        return data.map(item => ({
          id: item.id,
          name: item.name || '未知',
          disabled: false,
          subNodes: item.subTree ? this.formatStationCityTree(item.subTree) : [],
        }));
      },
      /**
       * 月份范围变化
       */
      handleMonthRangeChange(val) {
        if (val && val.length === 2) {
          const start = new Date(`${val[0]}-01`);
          const end = new Date(`${val[1]}-01`);
          const yearDiff = end.getFullYear() - start.getFullYear();
          const monthDiff = end.getMonth() - start.getMonth();
          const diffMonth = yearDiff * 12 + monthDiff;
          if (diffMonth > 5) {
            this.$message.warning('时间跨度最多6个月');
            const maxEnd = new Date(start);
            maxEnd.setMonth(maxEnd.getMonth() + 5);
            const format = d => {
              const year = d.getFullYear();
              const month = String(d.getMonth() + 1).padStart(2, '0');
              return `${year}-${month}`;
            };
            this.monthlyForm.monthRange = [val[0], format(maxEnd)];
          }
        }
      },
      /**
       * 月度电站选择变化
       */
      handleMonthlyStationChange() {
        this.monthlyForm.stationIds =
          (this.$refs.monthlyStationTree && this.$refs.monthlyStationTree.getCheckedKeys(true)) ||
          [];
      },
      /**
       * 周度电站选择变化
       */
      handleWeeklyStationChange() {
        this.weeklyForm.stationIds =
          (this.$refs.weeklyStationTree && this.$refs.weeklyStationTree.getCheckedKeys(true)) || [];
      },
      /**
       * 验证周范围
       */
      validateWeekRange() {
        const { weekStart, weekEnd } = this.weeklyForm;
        if (!weekStart || !weekEnd) {
          this.$message.warning('请选择周时间区间');
          return false;
        }
        if (weekStart.getTime() > weekEnd.getTime()) {
          this.$message.warning('开始周不能晚于结束周');
          return false;
        }
        const diffWeek = Math.ceil((weekEnd.getTime() - weekStart.getTime()) / (7 * 86400000));
        if (diffWeek > 29) {
          this.$message.warning('时间跨度最多30周');
          return false;
        }
        return true;
      },
      /**
       * 导出报表
       */
      exportReport(type) {
        if (type === 'monthly') {
          if (!this.monthlyForm.monthRange || this.monthlyForm.monthRange.length !== 2) {
            this.$message.warning('请选择月份时间区间');
            return;
          }
          this.monthlyExporting = true;
          offlineExport({
            downloadType: 'station_month_user',
            jsonNode: {
              beginMonth: this.monthlyForm.monthRange[0],
              endMonth: this.monthlyForm.monthRange[1],
              stationIdList: this.monthlyForm.stationIds,
            },
          });
          setTimeout(() => {
            this.monthlyExporting = false;
            this.$message.success('导出任务已提交，请前往下载中心查看');
          }, 1500);
        } else {
          if (!this.validateWeekRange()) {
            return;
          }
          this.weeklyExporting = true;
          offlineExport({
            downloadType: 'station_user_week_report',
            jsonNode: {
              startWeek: this.dateToWeekValue(this.weeklyForm.weekStart),
              endWeek: this.dateToWeekValue(this.weeklyForm.weekEnd),
              stationIdList: this.weeklyForm.stationIds,
            },
          });
          setTimeout(() => {
            this.weeklyExporting = false;
            this.$message.success('导出任务已提交，请前往下载中心查看');
          }, 1500);
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .user-operation-report {
    padding: 16px;

    ::v-deep .ykc-tabs {
      .el-tabs--border-card {
        border: 0;
        box-shadow: none;
      }

      .el-tabs__header {
        background: #fff;
        border-bottom: 1px solid #e5e7eb;
        margin-bottom: 0;
      }

      .el-tabs__nav {
        background: transparent;
      }

      .el-tabs--border-card > .el-tabs__header .el-tabs__item {
        border-right: 1px solid #e5e7eb;
        color: #6b7280;
        width: auto;
        min-width: 160px;
        padding: 0 24px !important;

        &:first-child {
          border-left: 1px solid #e5e7eb;
        }
      }

      .el-tabs--border-card > .el-tabs__header .el-tabs__item.is-active {
        color: #2563eb;
        background: #fff;
        border-bottom: 2px solid #2563eb;
      }

      .el-tabs--border-card > .el-tabs__header .el-tabs__item:not(.is-disabled):hover {
        color: #2563eb;
        background: #fff;
      }
    }

    .tab-panel {
      background: #fff;
      padding: 24px;
      border: 1px solid #e5e7eb;
      border-top: 0;
    }

    .panel-header {
      margin-bottom: 24px;

      .panel-title {
        font-size: 18px;
        font-weight: 500;
        color: #111827;
        margin: 0 0 8px 0;
      }

      .panel-desc {
        font-size: 14px;
        color: #6b7280;
        margin: 0;
      }
    }

    .filter-section {
      background: #f9fafb;
      border-radius: 8px;
      padding: 20px;
      margin-bottom: 24px;

      .section-title {
        font-size: 14px;
        font-weight: 500;
        color: #111827;
        margin-bottom: 16px;
      }

      .filter-row {
        display: flex;
        flex-wrap: wrap;
        gap: 24px;
      }

      .filter-item {
        display: flex;
        flex-direction: column;
        gap: 8px;
        min-width: 320px;

        .filter-label {
          font-size: 14px;
          color: #374151;
        }

        .filter-content {
          .week-range-wrap {
            display: flex;
            align-items: center;
            gap: 8px;

            .week-picker {
              width: 200px;
            }

            .week-tag {
              font-size: 13px;
              color: #2563eb;
              background: #eff6ff;
              padding: 2px 8px;
              border-radius: 4px;
              white-space: nowrap;
            }

            .range-separator {
              color: #9ca3af;
            }
          }

          .station-cascader {
            width: 320px;
          }
        }

        .filter-tip {
          font-size: 12px;
          color: #9ca3af;
          margin: 4px 0 0 0;
        }
      }
    }

    .export-section {
      display: flex;
      align-items: center;
      gap: 12px;

      .export-tip {
        font-size: 12px;
        color: #9ca3af;
      }
    }

    .tip-card {
      background: #fff;
      border-radius: 8px;
      padding: 16px 20px;
      margin-top: 16px;
      border: 1px solid #e5e7eb;

      .tip-content {
        display: flex;
        align-items: flex-start;
        gap: 12px;

        .tip-icon {
          color: #2563eb;
          font-size: 16px;
          margin-top: 2px;
        }

        .tip-text {
          .tip-title {
            font-size: 14px;
            font-weight: 500;
            color: #111827;
            margin: 0 0 8px 0;
          }

          .tip-list {
            margin: 0;
            padding-left: 16px;
            font-size: 13px;
            color: #6b7280;
            line-height: 1.8;
          }
        }
      }
    }
  }
</style>
